import { Box, Button, Container, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../contexts/ThemeContext';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import Step1CreateSite from './Step1CreateSite';
import Step2TenantId from './Step2TenantId';
import Step3ClientId from './Step3ClientId';
import Step4ClientSecret from './Step4ClientSecret';
import Step5SiteId from './Step5SiteId';
import Step6DriveId from './Step6DriveId';
import Step7Summary from './Step7Summary';

const steps = [
  'Create Site',
  'Tenant ID',
  'Client ID',
  'Client Secret',
  'Site ID',
  'Drive ID',
  'Summary & Verify',
];

interface SharepointWizardProps {
  redirectPath?: string;
}

const SharepointWizard: React.FC<SharepointWizardProps> = ({ redirectPath }) => {
  const theme = useTheme();
  const { themeMode } = useThemeMode();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [credentials, setCredentials] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: '',
    secretExpiryDate: '',
    siteUrl: '',
    siteId: '',
    driveId: '',
  });

  // Prompt the user if they try to leave with unsaved changes.
  useBeforeUnload(
    activeStep > 0 && activeStep < 7,
    'You have unsaved changes that will be lost. Are you sure you want to leave?'
  );

  const handleNext = (data?: Partial<typeof credentials>) => {
    if (data) {
      setCredentials((prev) => ({ ...prev, ...data }));
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleBackToAccount = () => {
    navigate(redirectPath || '/account');
  };

  const handleReturnToBidlogiq = () => {
    navigate('/dashboard');
  };

  const getStepContent = (step: number) => {
    const navProps = {
      activeStep: step,
      onBack: handleBack,
      onNext: handleNext,
      ...(step === 0 && { onBackToAccount: handleBackToAccount }),
    };
    switch (step) {
      case 0:
        return <Step1CreateSite {...navProps} credentials={credentials} />;
      case 1:
        return <Step2TenantId {...navProps} credentials={credentials} />;
      case 2:
        return <Step3ClientId {...navProps} credentials={credentials} />;
      case 3:
        return <Step4ClientSecret {...navProps} credentials={credentials} />;
      case 4:
        return (
          <Step5SiteId
            {...navProps}
            clientId={credentials.clientId}
            siteUrl={credentials.siteUrl}
            credentials={credentials}
          />
        );
      case 5:
        return <Step6DriveId {...navProps} siteId={credentials.siteId} credentials={credentials} />;
      case 6:
        return <Step7Summary {...navProps} credentials={credentials} redirectPath={redirectPath} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        padding: '40px 20px',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ padding: 4, textAlign: 'center' }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <img
              src={
                themeMode === 'dark'
                  ? '/Bidlogiq_Logo-Long_Reversed.png'
                  : '/Bidlogiq_Logo-Long_Full colours.png'
              }
              alt="BIDLOGIQ Logo"
              style={{ maxWidth: '300px' }}
            />
            <Button variant="outlined" onClick={handleReturnToBidlogiq}>
              Return to BIDLOGIQ
            </Button>
          </Box>
          <Typography variant="h4" component="h1" gutterBottom>
            How to Get Your SharePoint API Credentials for BIDLOGIQ
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
            This guide will walk you through obtaining the five key pieces of information required
            to connect BIDLOGIQ to your Microsoft 365 tenant.
            <br />
            <br />
            <strong>Important:</strong> The SharePoint site you connect here will act as your
            dedicated BIDLOGIQ processing engine. It will be used as a secure, isolated storage area
            where files from your various Knowledge Base collections are prepared for AI generation.
            <br />
            <br />
            Because of this, we strongly recommend creating a{' '}
            <strong>NEW, dedicated SharePoint site</strong> specifically for BIDLOGIQ rather than
            connecting an existing active team site. Once this core processing site is established,
            you can link your various Knowledge Base collections to any of your pre-existing
            SharePoint sites in the Knowledge Dashboard.
          </Typography>
          <Stepper
            activeStep={activeStep}
            sx={{
              pt: 3,
              pb: 5,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-around',
            }}
          >
            {steps.map((label) => (
              <Step
                key={label}
                sx={{
                  '& .Mui-completed .MuiStepIcon-root': {
                    color: 'success.main',
                  },
                }}
              >
                <StepLabel
                  sx={{
                    flexDirection: 'column',
                    '& .MuiStepLabel-iconContainer': {
                      paddingRight: 0, // Reset horizontal padding
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {getStepContent(activeStep)}
        </Paper>
        <Box sx={{ mt: 4, textAlign: 'center', color: 'text.primary' }}>
          <Typography variant="body2">
            If you have any questions, please contact us.
            <br />
            <strong>Email:</strong>{' '}
            <a href="mailto:support@bidlogiq.com" style={{ color: theme.palette.bidlogiq.brick }}>
              support@bidlogiq.com
            </a>
            <br />
            <strong>AUS:</strong> +61 (0)7 2143 6035
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SharepointWizard;
